import { Component, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import {
    Observable,
    Subject,
    finalize,
    map,
    takeUntil,
} from 'rxjs';
import { initializeEditor } from 'src/app/utils/editorConfig';
import { helper } from 'src/environments/helper';
import { BeneficiariService } from '../beneficiari.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { BeneficiarListDT, Beneficiar } from '../beneficiari.interface';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { StorageService } from 'src/app/services/storage.service';
import { environment } from 'src/environments/environment';
import { CommonService } from 'src/app/services/common.service';
import { DialogService } from 'src/app/services/dialog.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { OperatorAccess, UserFunction } from 'src/app/platform/nomenclatoare/operatori/operatori.interface';
import { OperatoriService } from 'src/app/platform/nomenclatoare/operatori/operatori.service';
import { AppFilesService } from 'src/app/services/app-files.service';

@Component({
    selector: 'app-beneficiari-detalii',
    templateUrl: './beneficiari-detalii.component.html',
    styleUrls: ['./beneficiari-detalii.component.scss']
})
export class BeneficiariDetaliiComponent implements OnInit, OnDestroy {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;
    helper = helper;

    private destroy$ = new Subject<void>();

    isLoading = false;
    editorConfig!: AngularEditorConfig;

    user: any = null;
    userFunctions: UserFunction[] = [];
    activeFunction: UserFunction | null = null;
    userAccesses: OperatorAccess[] = [];
    activeAccess: OperatorAccess | null = null;

    passedBeneficiarId!: number;
    beneficiar: Beneficiar | null = null;
    safeHtml!: SafeHtml;

    // ── Contracte expand/collapse ─────────────────────────────────────────────
    expandedContracts: boolean[] = [];
    allContractsExpanded = false;

    defaultChoiceParameter: { name: string; id: string } = {
        name: '-- Alegeți --',
        id: '-1',
    };

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            beneficiar: { id: number };
        },
        private dialogRef: MatDialogRef<BeneficiariDetaliiComponent>,
        private beneficiariService: BeneficiariService,
        private operatoriService: OperatoriService,
        private notificationService: NotificationService,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private authService: AuthService,
        private storageService: StorageService,
        private sanitizer: DomSanitizer,
        private commonService: CommonService,
        private appFilesService: AppFilesService,
        public toastr: ToastrService,
        public permissionsService: PermissionsService,
    ) {
        this.processUserDetails();
        this.passedBeneficiarId = this.data.beneficiar.id;
    }

    ngOnInit(): void {
        this.getBeneficiarInfo();
        this.editorConfig = initializeEditor();
        this.loadUserFunctions();
        this.loadUserAccess();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── User ─────────────────────────────────────────────────────────────────

    processUserDetails() {
        const userObj = this.storageService.getObject(environment.config.userKey);
        userObj
            .then((val) => { this.user = val; })
            .catch((err) => { console.log(err); });
    }

    loadUserFunctions(): void {
        this.operatoriService.userFunctions$
            .pipe(takeUntil(this.destroy$))
            .subscribe(userFunctions => {
                this.userFunctions = userFunctions;
                this.activeFunction = this.userFunctions.find(f => f.is_active) || null;
            });
    }

    loadUserAccess(): void {
        this.operatoriService.userAccess$
            .pipe(takeUntil(this.destroy$))
            .subscribe(userAccesses => {
                this.userAccesses = userAccesses;
                this.activeAccess = this.userAccesses.find(a => a.active) || null;
            });
    }

    // ── Beneficiar ───────────────────────────────────────────────────────────

    getBeneficiarInfo() {
        this.isLoading = true;
        this.beneficiariService
            .getBeneficiar(this.passedBeneficiarId)
            .pipe(map((response: any) => response.data))
            .subscribe({
                next: (beneficiarData: Beneficiar) => {
                    this.isLoading = false;
                    this.beneficiar = beneficiarData;
                    this.initContractsState();
                },
                error: (error: any) => {
                    this.isLoading = false;
                    console.error('Error', error);
                    let msg;
                    try {
                        msg = error.error.errors.message[0];
                    } catch {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService
                        .warningSwal(this.errorTitle, msg, this.errorIcon)
                        .then(() => { this.closeDialog(); });
                }
            });
    }

    onBeneficiarChanged(data: Beneficiar) {
        this.beneficiar = data;
        this.initContractsState();
    }

    closeDialog() {
        this.dialogRef?.close({ success: false });
    }

    onIsLoadingChange(event: boolean) {
        this.isLoading = event;
    }

    deleteBeneficiar() {
        Swal.fire({
            title: helper.swalTextConfig.commonTitles.areYouSure,
            text: helper.swalTextConfig.generalMessages.delete,
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.beneficiariService.deleteBeneficiar(this.passedBeneficiarId).subscribe({
                    next: () => {
                        this.closeDialog();
                        Swal.fire({
                            title: 'Șters!',
                            text: 'Beneficiar șters cu succes.',
                            icon: 'success',
                            confirmButtonText: 'OK',
                        });
                    },
                    error: (err: any) => {
                        let msg;
                        try {
                            // try to catch the message from server
                            msg = err.error.errors.message[0];
                        } catch (error) {
                            // controll output message
                            msg = helper.dialogConfig.generalMessages.error;
                        }
                        Swal.fire({
                            title: helper.dialogConfig.headers.errorAtentie,
                            text: msg,
                            icon: 'error',
                            confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        })
                    }
                });
            }
        });
    }

    downloadFisa() {
        this.isLoading = true;
        const fullPath = this.beneficiariService.downloadFisaUrl + this.passedBeneficiarId;

        this.appFilesService.downloadFile(fullPath)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('Download doc error: ', err);
                this.isLoading = false;
            })
    }

    // ── Contracte expand/collapse ─────────────────────────────────────────────

    initContractsState(): void {
        const len = this.beneficiar?.contracts?.length ?? 0;
        this.expandedContracts = Array(len).fill(false);
        this.allContractsExpanded = false;
    }

    toggleContract(index: number): void {
        this.expandedContracts[index] = !this.expandedContracts[index];
        this.allContractsExpanded = this.expandedContracts.every(v => v);
    }

    toggleAllContracts(): void {
        this.allContractsExpanded = !this.allContractsExpanded;
        this.expandedContracts = this.expandedContracts.map(() => this.allContractsExpanded);
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    formatDate(dateStr: string | null | undefined): string {
        if (!dateStr) return 'N/A';
        const [year, month, day] = dateStr.split('-');
        return `${day}.${month}.${year}`;
    }

    // ── Deschide contract în tab nou ─────────────────────────────────────────

    openContractDetails(contractId: number): void {
        // Transmitem id-ul direct prin URL, fără sessionStorage
        window.open(`/#/contracte-programari?contractId=${contractId}`, '_blank');
    }
}